import { useState } from "react";
import { Copy, Download, ExternalLink, MessageCircle, Send } from "lucide-react";
import { CURRENCY_SYMBOLS } from "../lib/currencies";

const cur = (code) => CURRENCY_SYMBOLS[code] || "€";
const hashtag = (str) => (str || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

// Build all three captions based on deal type
function buildCaptions(deal, url) {
  const s = cur(deal.price_currency);
  const dt = deal.deal_type || "flight";
  const period = deal.travel_window || deal.checkin_period || "";

  // helper: join non-empty lines, preserving intentional blank lines (null = skip, "" = blank)
  const lines = (...parts) =>
    parts.filter((l) => l !== undefined && l !== null).join("\n");

  // Unsplash credit — appended to Instagram + Facebook only (not WhatsApp)
  const credit = deal.photo_credit ? `📷 Photo: ${deal.photo_credit} on Unsplash` : null;

  if (dt === "hotel") {
    const city = deal.to_city || "";
    const priceStr = deal.price_per_person
      ? `from ${s}${deal.price_per_person}/night`
      : `from ${s}${deal.price_eur}/night`;
    const detailLine = [deal.board_type, deal.checkin_period || deal.travel_window]
      .filter(Boolean)
      .join(" · ");

    const instagram = lines(
      "🔴 RESTRICTED DEAL",
      deal.hotel_name || city,
      `${city}${deal.star_rating ? ` · ${deal.star_rating}★` : ""}`,
      priceStr,
      detailLine || undefined,
      "",
      "Link in bio 👆",
      "",
      `#hoteldeals #${hashtag(city)} #luxurytravel #travel #restricteddeals #hoteldeals`,
      credit ? "" : null,
      credit,
    );

    const facebook = lines(
      "New deal just dropped on Restricted Deals.",
      "",
      `${deal.hotel_name || city} in ${city} ${priceStr}.`,
      period ? `Travel period: ${period}.` : undefined,
      "",
      "Full details and booking link:",
      url,
      "",
      "Subscribe free at restricteddeals.com",
      credit ? "" : null,
      credit,
    );

    const whatsapp = lines(
      "🏨 RESTRICTED DEAL 🏨",
      "",
      `${deal.hotel_name || city} — ${city}`,
      priceStr,
      detailLine || undefined,
      "",
      "Full details 👇",
      url,
      "",
      "Subscribe free: restricteddeals.com/subscribe",
    );

    return { instagram, facebook, whatsapp };
  }

  if (dt === "cruise") {
    const depDest = deal.departure_port && deal.destination_port
      ? `${deal.departure_port} → ${deal.destination_port}`
      : deal.departure_port || deal.destination_port || "";
    const routeStr = deal.cruise_route || deal.to_city || "";
    const durationLine = [
      deal.duration_nights ? `${deal.duration_nights} nights` : undefined,
      deal.cabin_type || undefined,
    ]
      .filter(Boolean)
      .join(" · ");

    const instagram = lines(
      "🔴 RESTRICTED DEAL",
      deal.cruise_line || "Cruise",
      depDest || undefined,
      routeStr || undefined,
      `from ${s}${deal.price_eur} p.p.`,
      durationLine || undefined,
      "",
      "Link in bio 👆",
      "",
      "#cruisedeals #cruise #travel #restricteddeals #cruiselife",
      credit ? "" : null,
      credit,
    );

    const facebook = lines(
      "New deal just dropped on Restricted Deals.",
      "",
      `${deal.cruise_line || "Cruise"} cruise${depDest ? ` — ${depDest}` : ""} from ${s}${deal.price_eur} p.p.`,
      period ? `Travel period: ${period}.` : undefined,
      "",
      "Full details and booking link:",
      url,
      "",
      "Subscribe free at restricteddeals.com",
      credit ? "" : null,
      credit,
    );

    const whatsapp = lines(
      "🚢 RESTRICTED DEAL 🚢",
      "",
      `${deal.cruise_line || "Cruise"} cruise`,
      depDest || undefined,
      routeStr || undefined,
      `from ${s}${deal.price_eur} p.p.${deal.duration_nights ? ` · ${deal.duration_nights} nights` : ""}`,
      "",
      "Full details 👇",
      url,
      "",
      "Subscribe free: restricteddeals.com/subscribe",
    );

    return { instagram, facebook, whatsapp };
  }

  if (dt === "package") {
    const from = deal.from_city || "";
    const to = deal.to_city || "";
    const durationLine = [
      deal.duration_nights ? `${deal.duration_nights} nights` : undefined,
      deal.package_includes || undefined,
    ]
      .filter(Boolean)
      .join(" · ");

    const instagram = lines(
      "🔴 RESTRICTED DEAL",
      `${from} → ${to}`,
      `from ${s}${deal.price_eur} p.p.`,
      durationLine || undefined,
      "",
      "Link in bio 👆",
      "",
      `#traveldeals #${hashtag(to)} #travel #restricteddeals #packageholiday`,
      credit ? "" : null,
      credit,
    );

    const facebook = lines(
      "New deal just dropped on Restricted Deals.",
      "",
      `${from} to ${to} package from ${s}${deal.price_eur} p.p.`,
      period ? `Travel period: ${period}.` : undefined,
      "",
      "Full details and booking link:",
      url,
      "",
      "Subscribe free at restricteddeals.com",
      credit ? "" : null,
      credit,
    );

    const whatsapp = lines(
      "📦 RESTRICTED DEAL 📦",
      "",
      `${from} → ${to} package`,
      `from ${s}${deal.price_eur} p.p.`,
      durationLine || undefined,
      "",
      "Full details 👇",
      url,
      "",
      "Subscribe free: restricteddeals.com/subscribe",
    );

    return { instagram, facebook, whatsapp };
  }

  // ── Flight (default) ──────────────────────────────────────────────────────────────────────────
  const tripLabel =
    { round_trip: "RT", one_way: "OW", multi_city: "Multi" }[deal.trip_type] || "RT";
  const from = deal.from_city || "";
  const to = deal.to_city || "";
  const returnFrom = deal.return_from_city || deal.return_from_airport || "";
  const returnTo = deal.return_to_city || deal.return_to_airport || from;
  const _retKey = returnFrom.toUpperCase().split("(")[0].trim();
  const _toKey = (to || deal.to_airport || "").toUpperCase().split("(")[0].trim();
  const isOpenJaw = Boolean(returnFrom && _retKey !== _toKey);
  const routeStr = isOpenJaw ? `${from} → ${to} / ${returnFrom} → ${returnTo}` : `${from} → ${to}`;
  const flightMeta = [deal.airline, deal.travel_window].filter(Boolean).join(" · ");
  const flightInfo = [deal.stops, deal.baggage]
    .map((s) => (typeof s === "string" && s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .filter(Boolean)
    .join(" · ");

  const cabinHeader =
    deal.cabin_class === "business" ? "✈ Business Class" :
    deal.cabin_class === "first"    ? "✈ First Class"    : null;

  const instagram = lines(
    cabinHeader,
    "🔴 RESTRICTED DEAL",
    routeStr,
    `${s}${deal.price_eur} ${tripLabel}`,
    flightMeta || undefined,
    flightInfo || undefined,
    "",
    "Link in bio 👆",
    "",
    `#cheapflights #flightdeals #${hashtag(to)} #travel #restricteddeals #flightdeal`,
    credit ? "" : null,
    credit,
  );

  const facebook = lines(
    "New deal just dropped on Restricted Deals.",
    "",
    `${routeStr} for ${s}${deal.price_eur} ${tripLabel} on ${deal.airline}.`,
    period ? `Travel period: ${period}.` : undefined,
    "",
    "Full details and booking link:",
    url,
    "",
    "Subscribe free at restricteddeals.com",
    credit ? "" : null,
    credit,
  );

  const whatsapp = lines(
    cabinHeader,
    "✈️ RESTRICTED DEAL ✈️",
    "",
    routeStr,
    `${s}${deal.price_eur} ${tripLabel} on ${deal.airline}`,
    flightInfo || undefined,
    period ? `📅 Travel: ${period}` : undefined,
    "",
    "Full details 👇",
    url,
    "",
    "Subscribe free: restricteddeals.com/subscribe",
  );

  return { instagram, facebook, whatsapp };
}

async function downloadImage(imageUrl, filename) {
  try {
    const res = await fetch(imageUrl);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(imageUrl, "_blank");
  }
}

export default function ShareSheet({ deal, onBack, onTestDispatch }) {
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/deal/${deal.slug || deal.id}`
      : `https://restricteddeals.com/deal/${deal.slug || deal.id}`;

  const { instagram, facebook, whatsapp } = buildCaptions(deal, url);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(whatsapp)}`;

  const dealTitle =
    deal.deal_type === "hotel"
      ? deal.hotel_name || deal.to_city
      : deal.deal_type === "cruise"
      ? deal.cruise_line
      : deal.deal_type === "package"
      ? `${deal.from_city} → ${deal.to_city}`
      : `${deal.from_city} → ${deal.to_city}`;

  return (
    <div className="max-w-2xl pb-20" data-testid="share-sheet">
      {/* Header */}
      <div className="bg-[#1A1A1A] border-t-4 border-[#CC1A1A] px-6 py-5">
        <p className="eyebrow eyebrow-red">// SHARE THIS DEAL</p>
        <h1
          className="font-mono text-3xl text-white mt-2 uppercase tracking-tight leading-tight"
          data-testid="share-sheet-title"
        >
          {dealTitle}
        </h1>
      </div>

      {/* 1 · Deal photo */}
      {deal.image_url && (
        <div className="bg-[#1A1A1A] border-t border-[#2A2A2A]">
          <img src={deal.image_url} alt="" className="w-full max-h-72 object-cover" />
          <div className="px-6 py-4">
            <button
              onClick={() =>
                downloadImage(
                  deal.image_url,
                  `restricted-deal-${deal.slug || deal.id}.jpg`,
                )
              }
              className="btn-rd-outline w-full"
              data-testid="download-photo-btn"
            >
              <Download className="w-4 h-4" /> DOWNLOAD PHOTO
            </button>
          </div>
        </div>
      )}

      {/* 2 · Instagram caption */}
      <CaptionSection title="INSTAGRAM CAPTION" text={instagram} testId="caption-instagram" />

      {/* 3 · Facebook post */}
      <CaptionSection title="FACEBOOK POST" text={facebook} testId="caption-facebook" />

      {/* 4 · WhatsApp */}
      <div
        className="bg-[#1A1A1A] border-t border-[#2A2A2A] px-6 py-5"
        data-testid="caption-whatsapp"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="eyebrow eyebrow-red">// WHATSAPP MESSAGE</p>
          <CopyButton text={whatsapp} testId="caption-whatsapp-copy" />
        </div>
        <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#E5E0D8] break-words mb-4">
          {whatsapp}
        </pre>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-rd w-full justify-center"
          data-testid="share-to-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> OPEN IN WHATSAPP →
        </a>
      </div>

      {/* Deal URL */}
      <div className="bg-[#1A1A1A] border-t border-[#2A2A2A] px-6 py-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="eyebrow">// DEAL PAGE URL</p>
          <CopyButton text={url} testId="share-copy-url" />
        </div>
        <p className="font-mono text-sm text-[#9A8F83] break-all">{url}</p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="btn-rd-outline w-full mt-3 justify-center"
          data-testid="share-view-public"
        >
          <ExternalLink className="w-4 h-4" /> VIEW PUBLIC PAGE →
        </a>
      </div>

      {/* Actions */}
      <div className="mt-8 flex flex-col gap-3">
        {onTestDispatch && (
          <button onClick={onTestDispatch} className="btn-rd-outline w-full justify-center" data-testid="test-dispatch-btn">
            <Send className="w-4 h-4" /> TEST DISPATCH TO ADMIN
          </button>
        )}
        <button onClick={onBack} className="btn-rd" data-testid="share-back-btn">
          ← BACK TO FILES
        </button>
      </div>
    </div>
  );
}

function CaptionSection({ title, text, testId }) {
  return (
    <div className="bg-[#1A1A1A] border-t border-[#2A2A2A] px-6 py-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="eyebrow eyebrow-red">// {title}</p>
        <CopyButton text={text} testId={`${testId}-copy`} />
      </div>
      <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#E5E0D8] break-words">
        {text}
      </pre>
    </div>
  );
}

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`shrink-0 flex items-center gap-1.5 font-mono text-[0.65rem] tracking-[0.18em] uppercase px-3 py-2 border transition-colors ${
        copied
          ? "border-green-500 text-green-400 bg-green-500/10"
          : "border-[#CC1A1A] text-[#CC1A1A] hover:bg-[#CC1A1A] hover:text-white"
      }`}
      data-testid={testId}
    >
      {copied ? (
        "COPIED ✓"
      ) : (
        <>
          <Copy className="w-3 h-3" /> COPY
        </>
      )}
    </button>
  );
}
